import { IconButton, Stack } from '@fluentui/react';
import { useCallback } from 'react';

import { Grouping as H2OGPTeGrouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { Grouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { useTableData } from '../hooks/useTableData';
import { FormattedUsageDataPoint, TableDataRow } from '../types';
import { formatLocalDate } from '../utils';
import { UnitType } from './TimeSeriesStacked';
import { UsageDataTable } from './UsageDataTable';

interface TableContainerProps {
  data: FormattedUsageDataPoint[];
  groups: string[];
  groupBy: Grouping | H2OGPTeGrouping;
  isLoading: boolean;
  startTime: Date;
  endTime: Date;
  groupColors?: Record<string, string>;
  unitType?: UnitType;
}

/**
 * Generate CSV content from table data
 * Converts dash values back to 0 for CSV output
 */
function generateCsv(rows: TableDataRow[], timeColumns: string[], firstColumnHeader: string): string {
  const headers = [firstColumnHeader, ...timeColumns];
  const csvRows = [
    headers.join(','),
    ...rows.map((row) =>
      [
        row.group,
        ...timeColumns.map((t) => {
          const val = row[t];
          // Convert dash back to 0 for CSV
          return val === '-' ? '0' : val;
        }),
      ].join(',')
    ),
  ];
  return csvRows.join('\n');
}

/**
 * TableContainer - Wraps the usage data table with styling and data transformation
 *
 * Transforms chart data into table format and renders with consistent styling
 * matching the ChartContainer above it.
 */
export function TableContainer({
  data,
  groups,
  groupBy,
  isLoading,
  startTime,
  endTime,
  groupColors,
  unitType,
}: TableContainerProps) {
  const { rows, timeColumns, firstColumnHeader } = useTableData(data, groups, groupBy);

  const handleDownloadCsv = useCallback(() => {
    const csv = generateCsv(rows, timeColumns, firstColumnHeader);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ai_unit_consumption_${formatLocalDate(startTime)}_${formatLocalDate(
      endTime
    )}_${firstColumnHeader.toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [rows, timeColumns, firstColumnHeader, startTime, endTime]);

  const hasData = rows.length > 0 && timeColumns.length > 0;

  return (
    <div
      style={{
        background: 'white',
        borderRadius: 4,
        padding: '16px 24px',
        marginTop: 16,
      }}
    >
      {/* Header with download button */}
      <Stack horizontal horizontalAlign="end" styles={{ root: { marginBottom: 8 } }}>
        <IconButton
          iconProps={{ iconName: 'Download' }}
          title="Download as CSV"
          ariaLabel="Download as CSV"
          onClick={handleDownloadCsv}
          disabled={isLoading || !hasData}
          styles={{ icon: { color: 'black' } }}
        />
      </Stack>

      <UsageDataTable
        rows={rows}
        timeColumns={timeColumns}
        firstColumnHeader={firstColumnHeader}
        isLoading={isLoading}
        groupColors={groupColors}
        unitType={unitType}
      />
    </div>
  );
}
